import { computed } from 'vue'

import { defineStore } from 'pinia'

import { useCollection, useDocument, useFirestore } from 'vuefire'

import { collection, doc, increment, setDoc, updateDoc } from 'firebase/firestore'

import useOuraAuth from '#web-oura-tools/auth'

import { GITHUB_PROTOTYPE_ORG, PUBLISH_STAGES, stageIndex } from '#ourahub-intra/constants'

import type { Categories, Collections, Service } from '#ourahub-intra/types/services'
import type { Account, Preferences } from '#ourahub-intra/types/users'
import type { AppEnvironment } from '#web-oura-tools/types/auth'

export const useServicesStore = defineStore('ServicesStore', () => {
  const ouraAuth = useOuraAuth()

  const firestore = useFirestore()

  const { data: servicesRegistry, pending: servicesPending } = useCollection<Service>(() =>
    ouraAuth.user.value ? collection(firestore, 'service-registry') : null,
  )

  // Prototypes and prototype-management services are hidden from BPO users
  const servicesList = computed(() =>
    ouraAuth.user.value?.isBPOAgentUser
      ? (servicesRegistry.value ?? []).filter(
          (service) => !isPrototypeService(service) && !isPrototypeManagement(service),
        )
      : (servicesRegistry.value ?? []),
  )

  const { data: accountData, pending: accountDataPending } = useDocument<Account>(() =>
    ouraAuth.user.value ? doc(collection(firestore, 'account-data'), ouraAuth.user.value.id) : null,
  )

  const { data: accountPrefs, pending: accountPrefsPending } = useDocument<Preferences>(() =>
    ouraAuth.user.value ? doc(collection(firestore, 'account-prefs'), ouraAuth.user.value.id) : null,
  )

  const { data: categoriesList, pending: categoriesPending } = useDocument<Categories>(
    () => (ouraAuth.user.value ? doc(collection(firestore, 'service-presets'), 'app-categories') : null),
    { once: true },
  )

  const { data: collectionsList, pending: collectionsPending } = useDocument<Collections>(
    () => (ouraAuth.user.value ? doc(collection(firestore, 'service-presets'), 'app-collections') : null),
    { once: true },
  )

  /**
   * Categories sorted by the user's personal app-open counts (most used first).
   * Categories with equal counts keep the default order.*/
  const sortedCategories = computed(() => {
    const categories = categoriesList.value?.core ?? []
    const openCounts = accountData.value?.stats?.categoryOpenCounts ?? {}

    return [...categories].sort((a, b) => (openCounts[b.name] ?? 0) - (openCounts[a.name] ?? 0))
  })

  function incrementCategoryStat(field: 'categoryOpenCounts' | 'categoryFilterCounts', category: string) {
    if (!ouraAuth.user.value) return

    updateDoc(doc(firestore, 'account-data', ouraAuth.user.value.id), {
      [`stats.${field}.${category}`]: increment(1),
    })

    updateDoc(doc(firestore, 'service-presets', 'app-categories'), {
      [`stats.${field}.${category}`]: increment(1),
    })
  }

  function trackServiceOpen(service: Service | string) {
    const category = (typeof service === 'string' ? getService(service) : service)?.category

    if (category) incrementCategoryStat('categoryOpenCounts', category)
  }

  function trackCategoryFilter(category: string) {
    incrementCategoryStat('categoryFilterCounts', category)
  }

  function getService(service: string) {
    return servicesList.value?.find((s) => s.id === service)
  }

  function getCategory(service: Service | string) {
    const category = typeof service === 'string' ? getService(service)?.category : service?.category

    return categoriesList.value?.core?.find((c) => c.name === category)
  }

  /**
   * User is a maintainer of the service (is the owner or is listed as an editor)
   */
  function isMaintainer(service: Service | string, owner?: boolean) {
    const email = ouraAuth.user.value?.email ?? ''

    const resolvedService = typeof service === 'string' ? getService(service) : service

    return resolvedService?.owner === email || (owner ? false : (resolvedService?.editors?.includes(email) ?? false))
  }

  /**
   * Service is published to the given environment (defaults to the current one)
   */
  function isPublished(service: Service | string, stage = ouraAuth.appEnv.value) {
    const resolvedService = typeof service === 'string' ? getService(service) : service

    return Boolean(resolvedService?.public === stage)
  }

  /**
   * Service is online in the given environment (defaults to the current one)
   */
  function isOnline(service: Service | string, stage = ouraAuth.appEnv.value) {
    const resolvedService = typeof service === 'string' ? getService(service) : service

    return Boolean(resolvedService?.entry?.[stage])
  }

  /**
   * Service is a prototype (created via Proto Starter)
   */
  function isPrototypeService(service: Service | string): boolean {
    const resolvedService = typeof service === 'string' ? getService(service) : service

    return Boolean(resolvedService?.proto)
  }

  /**
   * Service is used for prototype management (creating / updating prototypes)
   */
  function isPrototypeManagement(service: Service | string): boolean {
    const resolvedService = typeof service === 'string' ? getService(service) : service

    return resolvedService?.id.startsWith('outo-proto-') ?? false
  }

  /**
   * Service can be deleted by its owner (a prototype with a repo in the prototype org)
   */
  function isDeletable(service: Service | string): boolean {
    const resolvedService = typeof service === 'string' ? getService(service) : service

    if (!resolvedService) {
      return false
    }

    return isPrototypeService(resolvedService) && (resolvedService.github?.includes(GITHUB_PROTOTYPE_ORG) ?? false)
  }

  /**
   * Service is launchable from Oura Hub
   */
  function isLaunchable(service: Service | string) {
    service = typeof service === 'string' ? getService(service)! : service

    return isLaunchableInOuraHub(service) || isLaunchableInNewTab(service)
  }

  function isLaunchableInNewTab(service: Service | string) {
    service = typeof service === 'string' ? getService(service)! : service

    return service?.context === 'web-service-ssr'
  }

  function isLaunchableInOuraHub(service: Service | string) {
    service = typeof service === 'string' ? getService(service)! : service

    return service?.context === 'web-service-spa'
  }

  /**
   * Highest other environment the service is published to, which can be switched to from this OuraHub
   */
  function publishedVersion(service: Service | string, current = ouraAuth.appEnv.value): AppEnvironment | undefined {
    const resolvedService = typeof service === 'string' ? getService(service) : service

    return [...PUBLISH_STAGES]
      .reverse()
      .find(
        (stage) =>
          stage !== current &&
          stageIndex(stage) <= stageIndex(import.meta.env.VITE_APP_ENV) &&
          resolvedService?.public === stage &&
          isOnline(resolvedService, stage),
      )
  }

  /**
   * Highest environment, up to the OuraHub deployment env, where the service is published and online
   */
  function publicStage(service: Service | string): AppEnvironment | undefined {
    return [...PUBLISH_STAGES]
      .reverse()
      .find(
        (stage) =>
          stageIndex(stage) <= stageIndex(import.meta.env.VITE_APP_ENV) &&
          isPublished(service, stage) &&
          isOnline(service, stage),
      )
  }

  /**
   * Environments the user can launch the service in: all accessible ones for maintainers, else the public one
   */
  function launchStages(service: Service | string): AppEnvironment[] {
    if (!isMaintainer(service)) {
      const stage = publicStage(service)

      return stage && canAccessService(service, stage) ? [stage] : []
    }

    return PUBLISH_STAGES.filter(
      (stage) => stageIndex(stage) <= stageIndex(import.meta.env.VITE_APP_ENV) && canAccessService(service, stage),
    )
  }

  /**
   * Reason why the user can or cannot access the service.
   */
  function accessStatus(
    service: Service | string,
    stage = ouraAuth.appEnv.value,
  ): {
    hasAccess: boolean

    reason?: string
    possibleAction?: 'request-access-rights' | 'publish-settings' | 'open-published-version'
    publishedVersion?: AppEnvironment
    color: 'error' | 'warning' | 'success' | 'info'
  } {
    if (!isOnline(service, stage)) {
      return {
        hasAccess: false,
        reason: 'This app / tool is not available at the moment, this is probably an error please report this.',
        color: 'warning',
      }
    }

    if (!isPublished(service, stage) && !isMaintainer(service)) {
      const version = publishedVersion(service, stage)

      return {
        hasAccess: false,
        reason: 'This app / tool is currently under development, therefore only its maintainers can access it.',
        possibleAction: version ? 'open-published-version' : undefined,
        publishedVersion: version,
        color: 'warning',
      }
    }

    if (!hasAccessRights(service, stage)) {
      return {
        hasAccess: false,
        reason:
          'You do not have required access rights to this app / tool, see the permissions info below for required rights.',
        possibleAction: 'request-access-rights',
        color: 'error',
      }
    }

    if (!isPublished(service, stage) && isMaintainer(service)) {
      return {
        hasAccess: true,
        reason:
          'This version is private, and therefore this version of the app / tool is only accessible to maintainers.',
        possibleAction: 'publish-settings',
        color: 'info',
      }
    }

    return { hasAccess: true, color: 'success' }
  }

  function hasAccessRights(service: Service | string, stage = ouraAuth.appEnv.value) {
    if (ouraAuth.user.value?.isBypassingAccessRights) {
      return true
    }

    const resolvedService = typeof service === 'string' ? getService(service) : service

    const requiredRight = resolvedService?.rights?.[stage]

    return !requiredRight || Boolean(ouraAuth.user.value?.hasPermission(requiredRight))
  }

  function canAccessService(service: Service | string, stage = ouraAuth.appEnv.value) {
    service = typeof service === 'string' ? getService(service)! : service

    return (
      isOnline(service, stage) &&
      (isPublished(service, stage) || isMaintainer(service)) &&
      hasAccessRights(service, stage) &&
      isLaunchable(service)
    )
  }

  function isPinnedService(service: Service | string) {
    service = typeof service === 'string' ? getService(service)! : service

    return accountPrefs.value?.pinnedServices?.includes(service?.id) ?? false
  }

  function togglePinnedService(service: Service | string) {
    service = typeof service === 'string' ? getService(service)! : service

    if (ouraAuth.user.value) {
      const pinnedServices = accountPrefs.value?.pinnedServices ?? []

      const newPinnedServices = pinnedServices.includes(service.id)
        ? pinnedServices.filter((id) => id !== service.id)
        : [...pinnedServices, service.id]

      setDoc(
        doc(firestore, 'account-prefs', ouraAuth.user.value.id),
        { pinnedServices: newPinnedServices },
        { merge: true },
      )
    }
  }

  return {
    isLoading: computed(
      () =>
        !ouraAuth.user.value ||
        servicesPending.value !== false ||
        categoriesPending.value !== false ||
        collectionsPending.value !== false ||
        accountDataPending.value !== false ||
        accountPrefsPending.value !== false,
    ),

    servicesList,
    categoriesList,
    collectionsList,
    sortedCategories,

    getService,
    getCategory,

    accessStatus,
    isMaintainer,
    hasAccessRights,
    canAccessService,

    isOnline,
    isPublished,
    isDeletable,
    isLaunchable,
    isPinnedService,
    isPrototypeService,
    isPrototypeManagement,

    isLaunchableInNewTab,
    isLaunchableInOuraHub,

    publicStage,
    launchStages,

    trackServiceOpen,
    trackCategoryFilter,

    togglePinnedService,
  }
})
